/**
 * Sleep Feedback Service
 *
 * CRUD operations for sleep_feedback table.
 * Handles saving feedback, calculating scores, and updating counters.
 */

import { supabase } from '@/lib/supabase';
import {
  calculateCycleScore,
  getDaySegment,
  type SleepFeedback,
  type SleepCycleScore,
  type DaySegment,
} from '../lib/sleepScoring';

export interface SaveFeedbackParams {
  careEntryId: string;
  pregnancyId: string;
  babyIndex: number;
  feedback: SleepFeedback;
  /**
   * Gemessene Wachphase in Minuten, oder null wenn kein verwertbarer Messwert
   * vorliegt (Erfassungsluecke). Das Feedback selbst wird trotzdem gespeichert -
   * die Bewertung der Eltern ist gueltig, nur die Zeitmessung nicht.
   */
  wakeWindowMinutes: number | null;
  napDurationMinutes: number;
  sleepStartHour: number;
}

export interface FeedbackEntry {
  id: string;
  careEntryId: string;
  ease: string;
  timing?: string;
  fallAsleepDuration?: string;
  sleepContext: string[];
  contextTags: string[];
  wakeUpMood?: string;
  cycleScore: number | null;
  wakeWindowMinutes: number | null;
  daySegment: string | null;
  createdAt: string;
}

/**
 * Save sleep feedback and calculate cycle score.
 */
export async function saveSleepFeedback(params: SaveFeedbackParams): Promise<FeedbackEntry | null> {
  const { careEntryId, pregnancyId, babyIndex, feedback, wakeWindowMinutes, napDurationMinutes, sleepStartHour } = params;

  // Get current user
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  // Calculate score
  const cycleScore = calculateCycleScore(wakeWindowMinutes ?? 0, feedback, napDurationMinutes);
  const daySegment = getDaySegment(sleepStartHour);

  const { data, error } = await supabase
    .from('sleep_feedback')
    .upsert({
      care_entry_id: careEntryId,
      pregnancy_id: pregnancyId,
      user_id: user.id,
      baby_index: babyIndex,
      ease: feedback.ease,
      timing: feedback.timing ?? null,
      fall_asleep_duration: feedback.fallAsleepDuration ?? null,
      sleep_context: feedback.sleepContext ?? [],
      context_tags: feedback.contextTags ?? [],
      wake_up_mood: feedback.wakeUpMood ?? null,
      cycle_score: cycleScore,
      wake_window_minutes: wakeWindowMinutes,  // null bei Erfassungsluecke
      day_segment: daySegment,
    }, {
      onConflict: 'care_entry_id',
    })
    .select()
    .single();

  if (error) {
    console.error('Error saving sleep feedback:', error);
    return null;
  }

  return {
    id: data.id,
    careEntryId: data.care_entry_id,
    ease: data.ease,
    timing: data.timing ?? undefined,
    fallAsleepDuration: data.fall_asleep_duration ?? undefined,
    sleepContext: data.sleep_context ?? [],
    contextTags: data.context_tags ?? [],
    wakeUpMood: data.wake_up_mood ?? undefined,
    cycleScore: data.cycle_score,
    wakeWindowMinutes: data.wake_window_minutes,
    daySegment: data.day_segment,
    createdAt: data.created_at,
  };
}

/**
 * Load recent cycle scores for the adaptive algorithm.
 */
export async function loadCycleScores(
  pregnancyId: string,
  babyIndex: number,
  days: number = 7,
  daySegment?: string,
): Promise<SleepCycleScore[]> {
  const { data, error } = await supabase.rpc('get_sleep_cycle_scores', {
    p_pregnancy_id: pregnancyId,
    p_baby_index: babyIndex,
    p_days: days,
    p_day_segment: daySegment ?? null,
  });

  if (error) {
    console.error('Error loading cycle scores:', error);
    return [];
  }

  if (!data || !Array.isArray(data)) return [];

  return data.map((d: Record<string, unknown>) => ({
    score: Number(d.score) || 0,
    wakeWindowMinutes: Number(d.wake_window_minutes) || 0,
    daySegment: ((d.day_segment as string) || 'morning') as DaySegment,
    recencyWeight: Number(d.recency_weight) || 0.5,
    contextWeight: Number(d.context_weight) || 1,
    napDurationMinutes: Number(d.nap_duration_minutes) || 0,
  }));
}

/**
 * Update the wake-up mood feedback after baby wakes up.
 */
export async function updateWakeUpMood(
  careEntryId: string,
  mood: 'happy' | 'neutral' | 'crying',
): Promise<boolean> {
  const { error } = await supabase
    .from('sleep_feedback')
    .update({ wake_up_mood: mood })
    .eq('care_entry_id', careEntryId);

  if (error) {
    console.error('Error updating wake-up mood:', error);
    return false;
  }
  return true;
}

/**
 * Get feedback for a specific care entry.
 */
export async function getFeedbackForEntry(careEntryId: string): Promise<FeedbackEntry | null> {
  const { data, error } = await supabase
    .from('sleep_feedback')
    .select('*')
    .eq('care_entry_id', careEntryId)
    .maybeSingle();

  if (error || !data) return null;

  return {
    id: data.id,
    careEntryId: data.care_entry_id,
    ease: data.ease,
    timing: data.timing ?? undefined,
    fallAsleepDuration: data.fall_asleep_duration ?? undefined,
    sleepContext: data.sleep_context ?? [],
    contextTags: data.context_tags ?? [],
    wakeUpMood: data.wake_up_mood ?? undefined,
    cycleScore: data.cycle_score,
    wakeWindowMinutes: data.wake_window_minutes,
    daySegment: data.day_segment,
    createdAt: data.created_at,
  };
}
